import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getSelectedElements } from '../../../element/selection/selectedElementsSelector';
import { getLocationParentId, getLocationSection } from '../../../../common/elements/utils/elementPropertyUtils';

import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { BoardSections } from '../../../../common/boards/boardConstants';

/**
 * Hook to manage the inbox state vs selected elements state.
 * - When the sheet opens, deselect any currently selected elements
 * - When the sheet closes, deselect any currently selected elements in the inbox
 * - If any elements become selected, close the sheet (unless all selected elements are in the inbox)
 */
const useManageElementSelectionChanges = (
    currentBoardId: string,
    sheetIsOpen: boolean,
    deselectAll: () => void,
    dispatchCloseSheet: () => void,
) => {
    const selectedElements = useSelector(getSelectedElements) as Immutable.List<ImMNElement>;

    // when sheet opens, deselect any currently selected elements
    useEffect(() => {
        if (!sheetIsOpen) return;
        if (selectedElements.size === 0) return;

        deselectAll();
    }, [sheetIsOpen]);

    // when sheet closes, deselect any currently selected elements in the inbox
    useEffect(() => {
        if (sheetIsOpen) return;

        const selectedInboxElements = selectedElements.filter(
            (element) =>
                getLocationParentId(element) === currentBoardId && getLocationSection(element) === BoardSections.INBOX,
        );

        if (selectedInboxElements.size === 0) return;

        deselectAll();
    }, [sheetIsOpen]);

    // if any elements become selected, close the sheet (unless all selected elements are in the inbox)
    useEffect(() => {
        if (!sheetIsOpen) return;
        if (selectedElements.size === 0) return;

        const allSelectedElementsAreInInbox = selectedElements.every(
            (element) =>
                getLocationSection(element) === BoardSections.INBOX ||
                getLocationSection(element) === BoardSections.ATTACHED,
        );

        if (allSelectedElementsAreInInbox) return;

        dispatchCloseSheet();
    }, [selectedElements]);
};

export default useManageElementSelectionChanges;
